import {
  useMyAccountQuery,
  useMyPlaybooksQuery,
  useMySubscribedPlaybooksQuery,
  usePlaybookSetsQuery,
  useUnsubscribeFromPlaybookSetMutation,
} from "@qw/graphql";
import { PlaybookType } from "@qw/qw-common";
import { Button, Column, ColumnContent, Icon, Spinner } from "@qw/ui";
import { useNavigate } from "react-router";
import { PlaybookListItem } from "./playbook-list-item";
import { PlaybookSetCard } from "./playbook-set-card";

export const DiscoverMorePlaybooks = () => {
  const navigate = useNavigate();
  return (
    <div className="gap-2 p-6 text-sm rounded bg-muted text-muted-foreground text-center border">
      <Icon icon="how_to_reg" className="text-2xl mb-3" />
      <h3 className="text-base text-accent-foreground font-medium mb-2">All the profit with none of the work</h3>
      <p className="mb-4">Subscribe to other Producer&rsquo;s playbooks to copy their hard work to your Alby.</p>
      <Button variant="secondary" size="sm" onClick={() => navigate("/playbooks/discover")}>
        Discover more playbooks
      </Button>
    </div>
  );
};

export const PlaybooksIndex = () => {
  const navigate = useNavigate();
  const { data, loading } = useMyPlaybooksQuery();

  const { data: subsData } = useMySubscribedPlaybooksQuery();
  const { data: setsData } = usePlaybookSetsQuery();

  const [unsubscribeFromSet] = useUnsubscribeFromPlaybookSetMutation();

  const { data: meData } = useMyAccountQuery();
  const playbooks = data?.myPlaybooks ?? [];

  // Filter sets to only show subscribed ones
  const subscribedSets = (setsData?.playbookSets ?? []).filter((set) => set.isSubscribed);

  // Hide non-library playbook types from the subscribed-library surface. Subscribing
  // to a set pulls every playbook in the set into mySubscribedPlaybooks; without
  // this filter, a hidden step or template would foreground itself the moment a
  // user subscribed to a set that contained one. Owners keep visibility on their
  // own WorkflowStep playbooks below.
  const subscribedPlaybooks = (subsData?.mySubscribedPlaybooks ?? []).filter(
    (p) =>
      ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.WorkflowStep &&
      ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.Template
  );

  const handleUnsubscribeFromSet = async (setId: string) => {
    await unsubscribeFromSet({
      variables: { id: setId },
      refetchQueries: ["PlaybookSets", "MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
  };

  const handleViewSet = (setId: string) => {
    navigate(`/playbooks/discover/${setId}`);
  };

  if (loading && !playbooks?.length) {
    return (
      <div className="flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const hasAnyContent = subscribedSets.length > 0 || playbooks.length > 0 || subscribedPlaybooks.length > 0;

  return (
    <Column className="flex-auto bg-background">
      <ColumnContent className="p-4">
        {subscribedSets.length > 0 && (
          <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(340px,1fr))] mb-4">
            {subscribedSets.map((set) => (
              <PlaybookSetCard
                key={set.id}
                id={set.id}
                name={set.name}
                playbookCount={set.playbookCount}
                isSubscribed={set.isSubscribed}
                onSubscribe={() => {}}
                onUnsubscribe={handleUnsubscribeFromSet}
                onViewSet={handleViewSet}
              />
            ))}
          </div>
        )}

        <div className={hasAnyContent ? "grid gap-2 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]" : ""}>
          {/* Owned Playbooks */}
          {playbooks.map((p) => (
            <PlaybookListItem
              key={p.id}
              title={`${p.name || "(untitled)"}`}
              author={`${meData?.myAccount?.firstName ?? ""} ${meData?.myAccount?.lastName ?? ""}`}
              subscribers={p.subscribers}
              description={p.description ?? undefined}
              type={(p.type as PlaybookType | undefined) ?? PlaybookType.Workflow}
              isOwned={true}
              onClick={() => navigate(`/playbooks/${p.id}`)}
            />
          ))}

          {/* Subscribed Individual Playbooks */}
          {subscribedPlaybooks.map((p) => (
            <PlaybookListItem
              key={p.id}
              title={p.name || "(untitled)"}
              author={`${p.author?.firstName ?? ""} ${p.author?.lastName ?? ""}`}
              subscribers={p.subscribers}
              description={p.description ?? undefined}
              type={(p.type as PlaybookType | undefined) ?? PlaybookType.Workflow}
              subscribed={true}
              onClick={() => navigate(`/playbooks/${p.id}`)}
            />
          ))}
          <DiscoverMorePlaybooks />
        </div>
      </ColumnContent>
    </Column>
  );
};
